/**
 * TxHistory — collapsible recent transactions list below the swap card.
 * Shows the last N swaps with token pair, amount, status badge, and
 * Etherscan link. Persisted via localStorage.
 */

import type { TxHistoryEntry } from "@/hooks/useTxHistory";

interface TxHistoryProps {
  entries: TxHistoryEntry[];
  onClear: () => void;
}

export function TxHistory({ entries, onClear }: TxHistoryProps) {
  if (entries.length === 0) return null;

  return (
    <div className="mt-3 rounded-[--radius-lg] border border-[--border] bg-[--bg-elev] p-3 shadow-[--shadow-card]">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[12px] font-semibold text-[--text]">Recent transactions</h3>
        <button
          type="button"
          onClick={onClear}
          className="text-[11px] text-[--text-subtle] hover:text-[--text] transition"
        >
          Clear
        </button>
      </div>

      <ul className="flex flex-col gap-1">
        {entries.slice(0, 5).map((tx) => (
          <li key={tx.hash}>
            <a
              href={`https://etherscan.io/tx/${tx.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-2 rounded-[--radius-sm] px-2 py-1.5 text-[12px] hover:bg-[--bg-inset] transition"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[--text]">
                  {tx.amountIn} {tx.inputToken}
                </span>
                <span className="text-[--text-subtle]" aria-hidden="true">
                  &rarr;
                </span>
                <span className="text-[--text-muted] truncate">
                  {tx.expectedOut} {tx.outputToken}
                </span>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={tx.status} />
                <span className="num text-[10px] text-[--text-subtle]">
                  {timeAgo(tx.timestamp)}
                </span>
                <ExternalIcon />
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusBadge({ status }: { status: TxHistoryEntry["status"] }) {
  const styles = {
    pending: "bg-[--warning] text-[--bg]",
    confirmed: "bg-[--success] text-[--bg]",
    failed: "bg-[--danger] text-white",
  };
  const labels = {
    pending: "Pending",
    confirmed: "Confirmed",
    failed: "Failed",
  };
  return (
    <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function timeAgo(ts: number): string {
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function ExternalIcon() {
  return (
    <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[--text-subtle]">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
    </svg>
  );
}
